/**
 * Modal that explains what the SubContractorHub integration does and does
 * not bring across. Renders the static markdown at `scope-summary.md`
 * (Story 1.2 AC3, FR59) so updates are version-controlled.
 *
 * The markdown is loaded with Vite's built-in `?raw` query and rendered via
 * `react-markdown`. No `@tailwindcss/typography` plugin dependency — we use
 * a small set of utility classes scoped via `components` overrides on the
 * markdown renderer.
 */

import ReactMarkdown from 'react-markdown';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import scopeSummaryRaw from './scope-summary.md?raw';

interface ScopeSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScopeSummaryDialog({ open, onOpenChange }: ScopeSummaryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>What's included in this integration?</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          <ReactMarkdown
            components={{
              h2: ({ children }) => (
                <h2 className="mt-4 mb-2 text-base font-semibold text-foreground first:mt-0">
                  {children}
                </h2>
              ),
              ul: ({ children }) => (
                <ul className="space-y-1.5 text-sm text-muted-foreground">{children}</ul>
              ),
              li: ({ children }) => (
                <li className="pl-2 [&::before]:content-['•'] [&::before]:mr-2 [&::before]:text-foreground">
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <strong className="font-medium text-foreground">{children}</strong>
              ),
            }}
          >
            {scopeSummaryRaw}
          </ReactMarkdown>
        </div>
      </DialogContent>
    </Dialog>
  );
}
